import React from 'react';
import { Search, X } from 'lucide-react';
import { DateFilter, ValidationFilter, Status, PaymentMethod } from '../types';
import { MultiSelectFilter } from './MultiSelectFilter';
import { getStatusColor, getPaymentMethodColor } from '../utils/statusColors';
interface FilterToolbarProps {
  dateFilter: DateFilter;
  onDateFilterChange: (filter: DateFilter) => void;
  validationFilters: ValidationFilter[];
  onValidationFilterChange: (filters: ValidationFilter[]) => void;
  statusFilters: string[];
  onStatusFiltersChange: (filters: string[]) => void;
  paymentMethodFilters: string[];
  onPaymentMethodFiltersChange: (filters: string[]) => void;
  commissionModelFilters: string[];
  onCommissionModelFiltersChange: (filters: string[]) => void;
  hideZeroCommission: boolean;
  onHideZeroCommissionChange: (hide: boolean) => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
}
const monthAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function buildDateOptions() {
  const today = new Date();
  const options: {
    label: string;
    value: string;
  }[] = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    options.push({
      label: monthAbbr[d.getMonth()],
      value: `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`
    });
  }
  return options;
}
const dateOptions = buildDateOptions();
const statusOptionList: Status[] = ['Started', 'In Process', 'Validated & Locked', 'Sent to LO for Validation', 'Pending Revision', 'Pending Level 2 Sign-off', 'Pending Payment', 'Paid by ACH', 'Paid by Check', 'Paid by Wire'];
const paymentMethodOptionList: PaymentMethod[] = ['Wire', 'ACH', 'Check'];
export function FilterToolbar({
  dateFilter,
  onDateFilterChange,
  validationFilters,
  onValidationFilterChange,
  statusFilters,
  onStatusFiltersChange,
  paymentMethodFilters,
  onPaymentMethodFiltersChange,
  commissionModelFilters,
  onCommissionModelFiltersChange,
  hideZeroCommission,
  onHideZeroCommissionChange,
  searchTerm,
  onSearchTermChange
}: FilterToolbarProps) {
  const toggleValidation = (filter: ValidationFilter) => {
    if (validationFilters.includes(filter)) {
      onValidationFilterChange(validationFilters.filter(f => f !== filter));
    } else {
      onValidationFilterChange([...validationFilters, filter]);
    }
  };
  const statusOptions = statusOptionList.map(s => ({
    value: s as string,
    label: s as string,
    colorClass: getStatusColor(s).split(' ').slice(0, 2).join(' ') // bg + text only, no hover
  }));
  const paymentMethodOptions = paymentMethodOptionList.map(m => ({
    value: m as string,
    label: m as string,
    colorClass: getPaymentMethodColor(m).split(' ').slice(0, 2).join(' ')
  }));
  const commissionModelOptions = [{
    value: 'Flat Fee',
    label: 'Flat Fee'
  }, {
    value: 'Standard',
    label: 'Standard'
  }];
  const hasAnyFilter = statusFilters.length > 0 || paymentMethodFilters.length > 0 || commissionModelFilters.length > 0 || validationFilters.length !== 2 || searchTerm.length > 0;
  const clearAllFilters = () => {
    onStatusFiltersChange([]);
    onPaymentMethodFiltersChange([]);
    onCommissionModelFiltersChange([]);
    onValidationFilterChange(['Validated', 'Unvalidated']);
    onSearchTermChange('');
  };
  return <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-id="element-149">
      <div className="flex flex-wrap items-center gap-4" data-id="element-150">
        {/* Date Range Pills */}
        <div className="flex flex-wrap gap-2" data-id="element-151">
          {dateOptions.map(option => <button key={option.value} onClick={() => onDateFilterChange(option.value)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${dateFilter === option.value ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-152">
              {option.label}
            </button>)}
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-slate-200" data-id="element-153" />

        {/* Status Filter */}
        <MultiSelectFilter label="Status" options={statusOptions} selected={statusFilters} onChange={onStatusFiltersChange} data-id="element-154" />

        {/* Payment Method Filter */}
        <MultiSelectFilter label="Payment Method" options={paymentMethodOptions} selected={paymentMethodFilters} onChange={onPaymentMethodFiltersChange} data-id="element-155" />

        {/* Commission Model Filter */}
        <MultiSelectFilter label="Commission Model" options={commissionModelOptions} selected={commissionModelFilters} onChange={onCommissionModelFiltersChange} data-id="element-156" />

        {/* Validation Chips */}
        <div className="flex gap-2" data-id="element-157">
          <button onClick={() => toggleValidation('Validated')} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${validationFilters.includes('Validated') ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-158">
            Validated
          </button>
          <button onClick={() => toggleValidation('Unvalidated')} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${validationFilters.includes('Unvalidated') ? 'bg-amber-100 text-amber-700 border border-amber-300' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-159">
            Unvalidated
          </button>
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-slate-200" data-id="element-160" />

        {/* Hide Zero Toggle */}
        <label className="flex items-center gap-2 cursor-pointer" data-id="element-161">
          <input type="checkbox" checked={hideZeroCommission} onChange={e => onHideZeroCommissionChange(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-162" />
          <span className="text-sm font-medium text-slate-700" data-id="element-163">
            Hide $0 commissions
          </span>
        </label>

        {hasAnyFilter && <button onClick={clearAllFilters} className="text-sm text-slate-500 hover:text-slate-700 font-medium flex items-center gap-1" data-id="element-164">
            <X className="w-4 h-4" data-id="element-165" />
            Clear filters
          </button>}

        {/* Search */}
        <div className="flex-1 min-w-[200px]" data-id="element-166" />
        <div className="relative" data-id="element-167">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-168" />
          <input type="text" value={searchTerm} onChange={e => onSearchTermChange(e.target.value)} placeholder="Search employees..." className="pl-9 pr-9 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64" data-id="element-169" />
          {searchTerm && <button onClick={() => onSearchTermChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-170">
              <X className="w-4 h-4" data-id="element-171" />
            </button>}
        </div>
      </div>
    </div>;
}